import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useUploadQueue } from '@/hooks/useUploadQueue';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { WifiOff, Wifi, RefreshCw, CheckCircle2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getPendingCount } from '@/lib/offlineQueue';

export function OfflineStatusBar() {
  const isOnline = useNetworkStatus();
  const { pendingCount: uploadPendingCount } = useUploadQueue();
  const { isSyncing, pendingCount: mutationPendingCount } = useOfflineSync();
  const [pendingMutations, setPendingMutations] = useState(0);

  // Update pending mutation count
  useEffect(() => {
    const updateCount = async () => {
      const count = await getPendingCount();
      setPendingMutations(count);
    };
    
    updateCount();
    const interval = setInterval(updateCount, 2000); // Update every 2 seconds
    return () => clearInterval(interval);
  }, []);

  // Show status bar if offline, or if there are pending uploads/mutations
  const hasPendingItems = uploadPendingCount > 0 || pendingMutations > 0;
  
  if (isOnline && !hasPendingItems && !isSyncing) return null;

  // Determine status message
  let statusMessage = '';
  let statusColor = '';
  let statusIcon = null;

  if (!isOnline) {
    statusMessage = 'Offline – showing last saved data';
    statusColor = 'bg-red-600 text-white';
    statusIcon = <WifiOff className="inline h-4 w-4 mr-1" />;
  } else if (isSyncing) {
    statusMessage = `Syncing ${pendingMutations} pending update${pendingMutations !== 1 ? 's' : ''}…`;
    statusColor = 'bg-blue-600 text-white';
    statusIcon = <RefreshCw className="inline h-4 w-4 mr-1 animate-spin" />;
  } else if (pendingMutations > 0) {
    statusMessage = `${pendingMutations} pending update${pendingMutations !== 1 ? 's' : ''} waiting to sync`;
    statusColor = 'bg-yellow-500 text-white';
    statusIcon = <Wifi className="inline h-4 w-4 mr-1" />;
  } else if (uploadPendingCount > 0) {
    statusMessage = `${uploadPendingCount} item${uploadPendingCount !== 1 ? 's' : ''} uploading`;
    statusColor = 'bg-yellow-500 text-white';
    statusIcon = <Wifi className="inline h-4 w-4 mr-1" />;
  } else {
    statusMessage = 'All changes synced';
    statusColor = 'bg-green-600 text-white';
    statusIcon = <CheckCircle2 className="inline h-4 w-4 mr-1" />;
  }

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 px-4 py-2 text-center text-sm font-medium ${statusColor}`}>
      {statusIcon}
      {statusMessage}
    </div>
  );
}
